import { useState } from "react";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import type { BackgroundJobKind } from "@/api/background-job/_models/BackgroundJob";

interface JobRunPickerProps {
  kinds: BackgroundJobKind[];
  selected: string | null;
  onSelect: (kind: string) => void;
}

/** The run drawer's job list: a search box over every job kind, the chosen one highlighted. */
export const JobRunPicker = (props: JobRunPickerProps) => {
  const { kinds, selected, onSelect } = props;
  const [search, setSearch] = useState("");
  const query = search.trim().toLowerCase();
  const matches = kinds.filter((kind) => !query || kind.label.toLowerCase().includes(query));

  return (
    <Box>
      <TextField
        fullWidth
        onChange={(event) => setSearch(event.target.value)}
        placeholder={`Search ${kinds.length} jobs…`}
        size="small"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
        value={search}
      />
      <List
        aria-label="Jobs"
        dense
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          maxHeight: 320,
          mt: 1,
          overflowY: "auto",
          p: 0.5,
        }}
      >
        {matches.map((kind) => (
          <ListItemButton
            key={kind.kind}
            onClick={() => onSelect(kind.kind)}
            selected={kind.kind === selected}
            sx={{ borderRadius: 0.75 }}
          >
            <Typography
              noWrap
              sx={{ fontWeight: 600 }}
              variant="body2"
            >
              {kind.label}
            </Typography>
          </ListItemButton>
        ))}
        {matches.length === 0 && (
          <Typography
            color="text.secondary"
            sx={{ p: 1.5 }}
            variant="body2"
          >
            No jobs match “{search}”.
          </Typography>
        )}
      </List>
    </Box>
  );
};
